import { useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch, FiAlertCircle, FiCheckCircle, FiInfo } from "react-icons/fi";

import Input from "@components/Input";
import HeadlessDialog from "@components/Dialog";
import { editHours, getEditHours } from "@service/HoursUploadService";
import { format } from "date-fns";
import { IHoursEditDTO } from "@interfaces/IHoursEditDTO";
import Button from "@components/Button";
import { getNextSaturday, getPreviousSunday } from "@utils/general";
import FlowbiteDatepickerInLine from "@components/DatepickerInLine";
import { IPlantSelect } from "@view/admin/HoursUpload";
import { SelectFilter } from "@components/SelectFilter";
import { useAuth } from "@components/context/AuthContext";
import { IRoleDTO } from "@interfaces/IRoleDTO";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";

interface IHoursEdit {
  plants: IPlantSelect[];
  roles: IRoleDTO[];
  refresh: boolean;
}
interface IHours {
  hours: IHoursEditDTO[];
  hoursCopy: IHoursEditDTO[];
}

const MAX_HOURS_PER_DAY = 18;

function parseHoursValue(params: any): string {
  if (
    params.newValue === null ||
    params.newValue === undefined ||
    params.newValue === "" ||
    isNaN(Number(params.newValue))
  ) {
    return "0.00";
  }
  if (Number(params.newValue) < 0 || Number(params.newValue) > MAX_HOURS_PER_DAY) {
    return "0.00";
  }
  return Number(params.newValue).toFixed(2).toString();
}

export function HoursEdit({ plants, roles, refresh }: IHoursEdit): JSX.Element {
  const { hasPermission } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hoursNew, setHours] = useState<IHours>({
    hours: [],
    hoursCopy: [],
  });
  const [start_date, setStartDate] = useState<Date | null>(
    getPreviousSunday(null, -1)
  );
  const [end_date, setEndDate] = useState<Date | null>(
    getNextSaturday(new Date(), -1)
  );
  const [selectedPlant, setSelectedPlant] = useState<IPlantSelect | null>(null);
  const disabledDates = hasPermission(roles, "Hours Edit", "Change Dates")
    ? false
    : true;
  const disabledPlants = hasPermission(roles, "Hours Edit", "Change Plant")
    ? false
    : true;

  const [alertModal, setAlertModal] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: "",
    message: "",
  });

  function showAlert(title: string, message: string) {
    setAlertModal({ open: true, title, message });
  }

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenuPrevention = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    const selected = plants && plants.length > 0 ? plants[0] : null;
    setSelectedPlant(selected);
  }, [plants]);

  useEffect(() => {
    setIsLoading(true);
  }, [refresh]);

  useEffect(() => {
    async function fetchPlants() {
      setIsLoading(false);
      setsearchVal("");
      try {
        const data = {
          start_date: format(
            start_date ? start_date : new Date(),
            "yyyy-MM-dd"
          ),
          end_date: format(end_date ? end_date : new Date(), "yyyy-MM-dd"),
          plant_id:
            selectedPlant?.id !== undefined
              ? String(selectedPlant.id)
              : undefined,
        };
        const response = await getEditHours(data);
        if (response.data) {
          const hoursCopy: IHours = {
            hours: response.data.map((hour: IHoursEditDTO, index: number) => ({
              ...hour,
              index,
            })),
            hoursCopy: response.data.map(
              (hour: IHoursEditDTO, index: number) => ({ ...hour, index })
            ),
          };
          setHours(hoursCopy);
        }
      } catch (error) {
        console.error("Error fetching plants:", error);
      }
    }
    if (isLoading && selectedPlant && start_date && end_date) {
      fetchPlants();
    }
  }, [isLoading, start_date, end_date, selectedPlant]);

  const pageSize = 9;
  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
    filter: true,
  };
  const [searchVal, setsearchVal] = useState("");

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      setsearchVal(val);
      gridApi!.setQuickFilter(val);
    }
  };
  const columnDefs = [
    {
      headerName: "Temp Id",
      field: "temp_number",
      width: 90,
      filter: false,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Contractor Id",
      field: "contractor_id",
      width: 120,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Name",
      field: "name",
      width: 150,
      filter: true,
    },
    {
      headerName: "Sun",
      field: "sun_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.sun_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.sun_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Mon",
      field: "mon_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.mon_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.mon_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Tue",
      field: "tue_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.tue_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.tue_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Wed",
      field: "wed_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.wed_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.wed_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Thur",
      field: "thu_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.thu_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.thu_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Fri",
      field: "fri_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.fri_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.fri_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      headerName: "Sat",
      field: "sat_hour",
      width: 80,
      filter: false,
      editable: true,
      cellEditor: "agTextCellEditor",
      cellStyle: { textAlign: "center" },
      valueParser: parseHoursValue,
      cellRenderer: (params: any): JSX.Element => {
        const old = Number(hoursNew.hoursCopy[params.data.index]?.sat_hour)
          .toFixed(2)
          .toString();
        const value = Number(hoursNew.hours[params.data.index]?.sat_hour)
          .toFixed(2)
          .toString();
        return (
          <div
            className={
              value !== old
                ? "tw-border-[1px] tw-bg-orange-500 tw-text-white tw-rounded-lg"
                : ""
            }
          >
            {value}
          </div>
        );
      },
    },
  ];

  function handleStartDateChange(date: Date) {
    if (date.getDay() !== 0) {
      showAlert("Invalid Date", "Please select a Sunday.");
      return;
    }
    setStartDate(date);
    const nextSaturday = getNextSaturday(date ? date : new Date());
    setEndDate(nextSaturday);
    setIsLoading(true);
  }

  function handleEndDateChange(date: Date | null) {
    if (!date) {
      setEndDate(null);
      return;
    }
    if (date.getDay() !== 6) {
      showAlert("Invalid Date", "Please select a Saturday.");
      return;
    }
    const previousSunday = getPreviousSunday(date);
    handleStartDateChange(previousSunday);
  }

  function handleChangePlant(value: IPlantSelect) {
    setSelectedPlant(value);
    setIsLoading(true);
  }

  async function handleSubmit() {
    setsearchVal("");
    gridApi!.setQuickFilter("");
    const HoursUpdated = hoursNew.hours.filter((hour, index) => {
      const original = hoursNew.hoursCopy[index];
      if (
        original.sun_hour !== hour.sun_hour ||
        original.mon_hour !== hour.mon_hour ||
        original.tue_hour !== hour.tue_hour ||
        original.wed_hour !== hour.wed_hour ||
        original.thu_hour !== hour.thu_hour ||
        original.fri_hour !== hour.fri_hour ||
        original.sat_hour !== hour.sat_hour
      ) {
        return true;
      }
      return false;
    });
    if (!HoursUpdated || HoursUpdated.length <= 0) {
      showAlert("No Changes", "There are no changes to submit.");
      return;
    }
    try {
      if (!start_date || !end_date) {
        showAlert("Invalid Date", "Please select a valid start and end date.");
        return;
      }
      const sendData = {
        hours: HoursUpdated,
        start: format(start_date, "yyyy-MM-dd"),
        end: format(end_date, "yyyy-MM-dd"),
      };
      await editHours(sendData);
      setIsLoading(true);
      showAlert("Success", "Hours Edited successfully!");
    } catch (err: any) {
      console.error("Upload Hours error:", err);
      const message = err?.response?.data?.error || "An unexpected error occurred.";
      showAlert("Error", message);
    }
  }
  return (
    <>
      <div className="tw-flex tw-rows tw-justify-between tw-items-center tw-mb-2">
        <h4 className="Subheader tw-mb-3">Edit Hours</h4>
        <div className="tw-flex tw-items-center tw-justify-end">
          <div className="tw-flex tw-justify-end tw-w-[410px]">
            <div className="tw-flex tw-ml-1 tw-mr-2 tw-items-center">
              Plant:
            </div>
            <SelectFilter
              className=""
              select={plants.length > 0 ? plants[0] : { id: 0, value: "" }}
              options={plants}
              onSelect={handleChangePlant}
              placeholder="Select a plant"
              disabled={disabledPlants}
            />
          </div>
        </div>
      </div>
      <div className="tw-flex tw-mb-2 tw-h-[3.5vh]">
        <Input
          icon={FiSearch}
          containerStyle={{ marginRight: 4 }}
          id="search"
          type="text"
          name="search"
          placeholder="search"
          onChange={(e: any) => updateSearchQuery(e.target.value)}
          disabled={false}
          value={searchVal}
        />
        <div className="tw-flex tw-min-w-[410px] tw-items-center">
          <span className="tw-ml-1">Start: </span>
          <FlowbiteDatepickerInLine
            disabled={disabledDates}
            name={undefined}
            value={start_date}
            handleOnChange={handleStartDateChange}
            handleOnBlur={undefined}
            className="tw-w-[163px] tw-ml-2.5"
          />
          <span className="tw-ml-2">End: </span>
          <FlowbiteDatepickerInLine
            disabled={disabledDates}
            name={undefined}
            value={end_date}
            handleOnChange={handleEndDateChange}
            handleOnBlur={undefined}
            className="tw-w-[163px] tw-ml-2"
          />
        </div>
      </div>

      {!isLoading && (
        <>
          <div onContextMenu={handleContextMenuPrevention}>
            <AgGridReact
              className="ag-theme-balham"
              domLayout="autoHeight"
              columnDefs={columnDefs}
              rowHeight={40}
              rowData={hoursNew.hours}
              gridOptions={{}}
              rowSelection="multiple"
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onCellContextMenu={handleCellContextMenu}
              colResizeDefault="shift"
              animateRows
              pagination
              pivotPanelShow="always"
              paginationPageSize={pageSize}
              singleClickEdit={true}
            />
            {ContextMenuComponent}
          </div>
          <div className="tw-flex tw-justify-end tw-mt-6">
            <Button
              type="submit"
              disabled={false}
              className="md:tw-w-[11%]"
              onClick={handleSubmit}
            >
              Submit Hours
            </Button>
          </div>
        </>
      )}

      <HeadlessDialog
        title={alertModal.title}
        description={alertModal.message}
        isOpen={alertModal.open}
        setIsOpen={(open: boolean) => {
          if (!open) setAlertModal((prev) => ({ ...prev, open: false }));
        }}
        buttonText="OK"
        icon={
          alertModal.title === "Success" ? (
            <FiCheckCircle className="tw-text-green-500" size={24} />
          ) : alertModal.title === "Error" ? (
            <FiAlertCircle className="tw-text-red-500" size={24} />
          ) : (
            <FiInfo className="tw-text-blue-500" size={24} />
          )
        }
      >
        {null}
      </HeadlessDialog>
    </>
  );
}
